/**
 * Accountability Buddy page — /accountability
 */

import { useEffect, useState } from 'react'
import { Users2, Shuffle, Trash2, CheckCircle2, Clock, AlertCircle } from 'lucide-react'
import { api, AccountabilityPair, type MeProfile } from '../lib/api'
import { resolveBuddyCheckInAction } from '../lib/buddyCheckIn'
import { useAuthStore } from '../store/authStore'
import clsx from 'clsx'

const WARM_CARD = '#FDFCF8'
const WARM_BORDER = '#e0d9cc'

export function AccountabilityPage() {
  const { user } = useAuthStore()
  const isManager = user?.role === 'manager' || user?.role === 'admin'

  const [pairs, setPairs] = useState<AccountabilityPair[]>([])
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [runResult, setRunResult] = useState<{ pairsCreated: number; pairs: AccountabilityPair[] } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [meContext, setMeContext] = useState<Pick<MeProfile, 'preferred_channel' | 'slack_team_id'> | null>(null)

  async function loadPairs() {
    try {
      const res = await api.getAccountabilityPairs()
      setPairs((res as { pairs: AccountabilityPair[] }).pairs ?? [])
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load pairs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPairs()
    api
      .getMe()
      .then((me) =>
        setMeContext({
          preferred_channel: me.preferred_channel,
          slack_team_id: me.slack_team_id ?? null,
        })
      )
      .catch(() => setMeContext(null))
  }, [])

  async function handleRunMatching() {
    setRunning(true)
    setRunResult(null)
    setError(null)
    try {
      const res = await api.runAccountabilityMatching()
      setRunResult(res as { pairsCreated: number; pairs: AccountabilityPair[] })
      await loadPairs()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Matching failed')
    } finally {
      setRunning(false)
    }
  }

  async function handleDeactivate(pairId: string) {
    try {
      await api.deactivatePair(pairId)
      setPairs(prev => prev.filter(p => p.id !== pairId))
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not deactivate pair')
    }
  }

  const myPair = pairs.find(
    p => p.user_a_id === user?.id || p.user_b_id === user?.id
  )

  const trackLabel = (track: string) =>
    track.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-ink" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>Accountability Buddies</h1>
          <p className="text-sm mt-1" style={{color: '#6b8a8f'}}>
            Science-backed peer pairs to reinforce behavioral change
          </p>
        </div>
        {isManager && (
          <button
            onClick={handleRunMatching}
            disabled={running}
            className="flex items-center gap-2 text-white font-medium text-sm px-5 py-2 rounded-full transition-colors disabled:opacity-50 shadow-glow-teal"
            style={{ background: '#2A7A8A' }}
            onMouseEnter={e => (e.currentTarget.style.background = '#236b7a')}
            onMouseLeave={e => (e.currentTarget.style.background = '#2A7A8A')}
          >
            <Shuffle size={15} />
            {running ? 'Matching…' : 'Run Matching'}
          </button>
        )}
      </div>

      {runResult && (
        <div
          className="mb-6 flex items-center gap-3 rounded-xl px-4 py-3 border shadow-card"
          style={{ background: WARM_CARD, borderColor: '#2A7A8A', boxShadow: '0 0 24px -8px rgba(42,122,138,0.2)' }}
        >
          <CheckCircle2 size={16} className="text-brand-teal shrink-0" />
          <p className="text-sm text-ink-muted">
            Matching complete — <strong className="text-ink">{runResult.pairsCreated}</strong> new pairs created.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-6 flex items-center gap-3 bg-[#fdf2f2] border border-[#e8cfcf] rounded-xl px-4 py-3">
          <AlertCircle size={16} className="text-red-700 shrink-0" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="mb-8">
        <h2 className="text-xs font-bold text-ink-soft uppercase tracking-wider mb-3">Your Buddy</h2>
        {loading ? (
          <div className="bg-warm-card border border-warm-border rounded-2xl p-6 animate-pulse h-28 shadow-card" />
        ) : myPair ? (
          <MyBuddyCard
            pair={myPair}
            userId={user?.id ?? ''}
            trackLabel={trackLabel}
            preferredChannel={meContext?.preferred_channel}
            slackTeamId={meContext?.slack_team_id ?? null}
          />
        ) : (
          <div className="bg-warm-card border border-warm-border rounded-2xl p-6 flex items-center gap-4 shadow-card">
            <div className="w-10 h-10 rounded-xl bg-warm-inset border border-warm-border flex items-center justify-center shrink-0">
              <Users2 size={20} className="text-ink-soft" />
            </div>
            <div>
              <p className="text-sm font-medium text-ink">No buddy yet</p>
              <p className="text-xs text-ink-muted mt-0.5">
                {isManager
                  ? 'Run matching to create accountability pairs for your team.'
                  : 'Your manager will run buddy matching soon. Check back Monday morning!'}
              </p>
            </div>
          </div>
        )}
      </div>

      {isManager && (
        <div>
          <h2 className="text-xs font-bold text-ink-soft uppercase tracking-wider mb-3">
            All Active Pairs ({pairs.length})
          </h2>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-warm-card border border-warm-border rounded-xl p-4 animate-pulse h-16 shadow-card" />
              ))}
            </div>
          ) : pairs.length === 0 ? (
            <div className="bg-warm-card border border-warm-border rounded-xl p-6 text-center shadow-card">
              <p className="text-sm text-ink-muted">No active pairs yet. Run matching to create them.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pairs.map(pair => (
                <PairRow key={pair.id} pair={pair} trackLabel={trackLabel} onDeactivate={handleDeactivate} />
              ))}
            </div>
          )}
        </div>
      )}

      <div
        className="mt-10 rounded-2xl border p-6 shadow-card"
        style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
      >
        <h3 className="text-sm font-semibold text-ink mb-4">How accountability buddy matching works</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { step: '1', title: 'Weekly matching', desc: 'Every Monday at 8am, the algorithm pairs employees with similar nudge tracks and engagement scores.' },
            { step: '2', title: 'Slack notification', desc: 'Both buddies receive a DM introducing them and suggesting a short check-in during the week.' },
            { step: '3', title: 'Shared accountability', desc: 'Pairs are refreshed every 4 weeks so employees build broad support networks over time.' },
          ].map(({ step, title, desc }) => (
            <div key={step} className="flex gap-3">
              <div
                className="w-6 h-6 rounded-full border flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 text-brand-teal"
                style={{ background: '#EBF6FB', borderColor: '#a7d8e0' }}
              >
                {step}
              </div>
              <div>
                <p className="text-xs font-semibold text-ink mb-1">{title}</p>
                <p className="text-xs text-ink-muted leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function MyBuddyCard({
  pair,
  userId,
  trackLabel,
  preferredChannel,
  slackTeamId,
}: {
  pair: AccountabilityPair
  userId: string
  trackLabel: (t: string) => string
  preferredChannel: string | undefined
  slackTeamId: string | null
}) {
  const buddyName = pair.user_a_id === userId ? pair.user_b_name : pair.user_a_name
  const buddyEmail = pair.user_a_id === userId ? pair.user_b_email : pair.user_a_email
  const buddySlackId = pair.user_a_id === userId ? pair.user_b_slack_id : pair.user_a_slack_id
  const buddyTeamsId = pair.user_a_id === userId ? pair.user_b_teams_id : pair.user_a_teams_id
  const checkIn = buddyEmail
    ? resolveBuddyCheckInAction({
        preferredChannel,
        slackTeamId,
        buddySlackId: buddySlackId ?? undefined,
        buddyTeamsId: buddyTeamsId ?? undefined,
        buddyEmail,
      })
    : null
  const initial = (buddyName ?? buddyEmail ?? '?')[0]?.toUpperCase()

  const pairedDays = Math.floor(
    (Date.now() - new Date(pair.paired_at).getTime()) / (1000 * 60 * 60 * 24)
  )

  return (
    <div
      className="relative overflow-hidden rounded-2xl border p-6 shadow-card animate-breathe"
      style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
    >
      <div className="absolute inset-0 pointer-events-none glow-radial-teal opacity-70" aria-hidden />
      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-brand-teal flex items-center justify-center text-lg font-bold text-white shrink-0 shadow-glow-teal">
            {initial}
          </div>
          <div>
            <p className="text-sm font-semibold text-ink">{buddyName ?? buddyEmail}</p>
            {buddyEmail && buddyName && (
              <p className="text-xs text-ink-soft mt-0.5">{buddyEmail}</p>
            )}
            <div className="flex items-center gap-3 mt-2">
              {pair.nudge_track && (
                <span
                  className="text-xs px-2.5 py-0.5 rounded-full border font-medium"
                  style={{ background: '#EBF6FB', color: '#2A7A8A', borderColor: '#a7d8e0' }}
                >
                  {trackLabel(pair.nudge_track)} track
                </span>
              )}
              <span className="text-xs text-ink-soft flex items-center gap-1">
                <Clock size={11} />
                Paired {pairedDays === 0 ? 'today' : `${pairedDays}d ago`}
              </span>
            </div>
          </div>
        </div>
        {checkIn && (
          <a
            href={checkIn.href}
            {...(checkIn.external ? { target: '_blank', rel: 'noreferrer' as const } : {})}
            className="text-xs text-white font-medium px-4 py-1.5 rounded-full transition-colors shadow-glow-teal"
            style={{ background: '#2A7A8A' }}
            onMouseEnter={e => (e.currentTarget.style.background = '#236b7a')}
            onMouseLeave={e => (e.currentTarget.style.background = '#2A7A8A')}
          >
            {checkIn.label} →
          </a>
        )}
      </div>
    </div>
  )
}

function PairRow({ pair, trackLabel, onDeactivate }: {
  pair: AccountabilityPair
  trackLabel: (t: string) => string
  onDeactivate: (id: string) => void
}) {
  const [confirming, setConfirming] = useState(false)

  return (
    <div className="bg-warm-card border border-warm-border rounded-xl px-5 py-4 flex items-center justify-between gap-4 shadow-card">
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex -space-x-2 shrink-0">
          {[pair.user_a_name ?? pair.user_a_email, pair.user_b_name ?? pair.user_b_email].map((n, i) => (
            <div
              key={i}
              className={clsx(
                'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2',
                i === 0 ? 'bg-brand-teal text-white' : 'bg-brand-sage text-ink'
              )}
              style={{ borderColor: WARM_CARD }}
            >
              {(n ?? '?')[0]?.toUpperCase()}
            </div>
          ))}
        </div>
        <div className="min-w-0">
          <p className="text-sm text-ink truncate">
            {pair.user_a_name ?? pair.user_a_email} &amp; {pair.user_b_name ?? pair.user_b_email}
          </p>
          {pair.nudge_track && (
            <p className="text-xs text-ink-muted mt-0.5">{trackLabel(pair.nudge_track)} track</p>
          )}
        </div>
      </div>
      <div className="shrink-0">
        {confirming ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-muted">Deactivate?</span>
            <button
              type="button"
              onClick={() => { onDeactivate(pair.id); setConfirming(false) }}
              className="text-xs text-red-700 hover:text-red-800 font-medium"
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="text-xs text-ink-soft hover:text-ink"
            >
              No
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="text-ink-soft hover:text-red-700 transition-colors"
            title="Deactivate pair"
          >
            <Trash2 size={15} />
          </button>
        )}
      </div>
    </div>
  )
}
